import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styles from "./CookieBanner.module.css";
import { Cookie, Settings } from "lucide-react";

export default function CookieBanner({ forceShow, onClose }) {
  const { t } = useTranslation("privacy-policy");
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    if (forceShow) {
      setIsVisible(true);
      const cookieConsent = localStorage.getItem("cookieConsent");
      if (cookieConsent) {
        try {
          const consent = JSON.parse(cookieConsent);
          setPreferences({
            necessary: true,
            analytics: consent.analytics || false,
            marketing: consent.marketing || false,
          });
        } catch (error) {
          console.error("Error parsing cookie consent:", error);
        }
      }
    } else {
      const cookieConsent = localStorage.getItem("cookieConsent");
      if (!cookieConsent) {
        setIsVisible(true);
      }
    }
  }, [forceShow]);

  const handleAcceptAll = () => {
    const consent = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem("cookieConsent", JSON.stringify(consent));
    setIsVisible(false);
    setShowSettings(false);
    if (onClose) onClose();

    // Initialize analytics/marketing cookies here
    initializeAnalytics();
  };

  const handleAcceptSelected = () => {
    const consent = {
      ...preferences,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem("cookieConsent", JSON.stringify(consent));
    setIsVisible(false);
    setShowSettings(false);
    if (onClose) onClose();

    if (preferences.analytics) {
      initializeAnalytics();
    }
  };

  const handleRejectAll = () => {
    const consent = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem("cookieConsent", JSON.stringify(consent));
    setIsVisible(false);
    setShowSettings(false);
    if (onClose) onClose();
  };

  const initializeAnalytics = () => {
    // Add your analytics initialization code here
    // For example: Google Analytics, Facebook Pixel, etc.
    console.log("Analytics initialized");
  };

  const handlePreferenceChange = (type) => {
    if (type === "necessary") return; // Can't change necessary cookies

    setPreferences((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  if (!isVisible) return null;

  return (
    <div className={styles.cookieBanner}>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.header}>
            <Cookie size={24} className={styles.icon} />
            <h3>{t("cookieBanner.title")}</h3>
          </div>

          {!showSettings ? (
            <>
              <p className={styles.description}>
                {t("cookieBanner.description")}
              </p>

              <div className={styles.buttons}>
                <button
                  onClick={handleAcceptAll}
                  className={`${styles.button} ${styles.acceptAll}`}
                >
                  {t("cookieBanner.buttons.acceptAll")}
                </button>
                <button
                  onClick={() => setShowSettings(true)}
                  className={`${styles.button} ${styles.settings}`}
                >
                  <Settings size={16} />
                  {t("cookieBanner.buttons.cookieSettings")}
                </button>
                <button
                  onClick={handleRejectAll}
                  className={`${styles.button} ${styles.reject}`}
                >
                  {t("cookieBanner.buttons.rejectAll")}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className={styles.settingsContent}>
                <h4>{t("cookieBanner.settings.title")}</h4>

                <div className={styles.cookieType}>
                  <div className={styles.cookieHeader}>
                    <label className={styles.cookieLabel}>
                      <input
                        type="checkbox"
                        checked={preferences.necessary}
                        disabled
                        className={styles.checkbox}
                      />
                      <span className={styles.checkboxLabel}>
                        {t("cookieBanner.settings.necessary.label")}
                      </span>
                      <span className={styles.required}>
                        {t("cookieBanner.settings.necessary.required")}
                      </span>
                    </label>
                  </div>
                  <p className={styles.cookieDescription}>
                    {t("cookieBanner.settings.necessary.description")}
                  </p>
                </div>

                <div className={styles.cookieType}>
                  <div className={styles.cookieHeader}>
                    <label className={styles.cookieLabel}>
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={() => handlePreferenceChange("analytics")}
                        className={styles.checkbox}
                      />
                      <span className={styles.checkboxLabel}>
                        {t("cookieBanner.settings.analytics.label")}
                      </span>
                    </label>
                  </div>
                  <p className={styles.cookieDescription}>
                    {t("cookieBanner.settings.analytics.description")}
                  </p>
                </div>

                <div className={styles.cookieType}>
                  <div className={styles.cookieHeader}>
                    <label className={styles.cookieLabel}>
                      <input
                        type="checkbox"
                        checked={preferences.marketing}
                        onChange={() => handlePreferenceChange("marketing")}
                        className={styles.checkbox}
                      />
                      <span className={styles.checkboxLabel}>
                        {t("cookieBanner.settings.marketing.label")}
                      </span>
                    </label>
                  </div>
                  <p className={styles.cookieDescription}>
                    {t("cookieBanner.settings.marketing.description")}
                  </p>
                </div>
              </div>

              <div className={styles.buttons}>
                <button
                  onClick={handleAcceptSelected}
                  className={`${styles.button} ${styles.acceptSelected}`}
                >
                  {t("cookieBanner.buttons.savePreferences")}
                </button>
                <button
                  onClick={() => setShowSettings(false)}
                  className={`${styles.button} ${styles.back}`}
                >
                  {t("cookieBanner.buttons.back")}
                </button>
              </div>
            </>
          )}

          <p className={styles.policyLink}>
            {t("cookieBanner.policyLink")}{" "}
            <a href="/privacy-policy">{t("cookieBanner.privacyPolicy")}</a>
          </p>
        </div>
      </div>
    </div>
  );
}
